import TauriSQL from '@tauri-apps/plugin-sql';
import type { RxDocumentData } from 'rxdb';

import { isDevOrTest } from '../../utils/environment';
import makeLogger from '../../utils/makeLogger';
import { type SQLiteQueryWithParams, type SQLResultRow, getRxStorageSQLite, SQLiteBasics } from './storages/storage-sqlite';

const logger = makeLogger(__filename);

type TauriSQLDataRow = { data: string; };
type TauriSQLCountRow = { count: number; };
type TauriSQLRxDBDocumentRow<T> = RxDocumentData<unknown> & { data: T; id: string; };
type TauriSQLDocumentResult = { id: string; data: string; };
// Result can be RxDB documents, count rows, or arbitrary raw query results (sqlite_master)
type TauriSQLQueryResult = TauriSQLDocumentResult[] | TauriSQLCountRow[] | { [key: string]: unknown; }[];

let transaction: SQLiteQueryWithParams[] | undefined;

const desktopSqliteBasics: SQLiteBasics<TauriSQL> = {
  journalMode: 'WAL2',

  async open(name: string): Promise<TauriSQL> {
    const path = `sqlite:${name}.sqlite`;
    logger.debug(`OPENING DB`, { name, path });
    const db = await TauriSQL.load(path);
    if (isDevOrTest) {
      // Attach DB to window for easy debugging
      // Explanation: intentional window augmentation for dev debugging — no safer pattern available for dynamic property assignment
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-type-assertion
      (window as any).RxDBSqlite = db;
    }
    await this.setPragma(db, 'synchronous', 'NORMAL');
    await this.setPragma(db, 'cache_size', '-200000'); // 200 MB
    await this.setPragma(db, 'temp_store', 'MEMORY');
    await this.setPragma(db, 'mmap_size', '1000000000');
    await this.setPragma(db, 'threads', '4');
    return db;
  },

  async all(
    db: TauriSQL,
    { query, params: _params, context }: SQLiteQueryWithParams,
  ) {
    const start = performance.now();
    let queryResult: TauriSQLQueryResult;
    const params = _params.map((param) => {
      if (typeof param === 'boolean') {
        return param ? 1 : 0;
      } else {
        return param;
      }
    });
    const rows = await db.select<TauriSQLCountRow[] | TauriSQLDataRow[]>(query, params);
    if (rows.length === 0) {
      queryResult = [];
    } else if ('count' in rows[0]) {
      // Explanation: rows is typed as the union; runtime 'count' check confirms which branch we are in
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      queryResult = rows as TauriSQLCountRow[];
    } else if ('data' in rows[0]) {
      // RxDB document rows with JSON data
      // Explanation: rows is typed as the union; runtime 'data' check confirms TauriSQLDataRow branch
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const rxdbDocRows = (rows as TauriSQLDataRow[]).map(
        // Explanation: JSON.parse returns unknown; we assert the shape we serialized and know the structure of
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        ({ data }) => JSON.parse(data) as TauriSQLRxDBDocumentRow<unknown>,
      );
      queryResult = rxdbDocRows.map((row) => ({ id: row.id, data: JSON.stringify(row) }));
    } else {
      // Raw query results (e.g., sqlite_master queries) - pass through as-is
      queryResult = rows as { [key: string]: unknown; }[];
    }
    const duration = performance.now() - start;
    logger.debug(`SELECT QUERY (${duration.toFixed()}ms): ${query}`, {
      query,
      queryResult,
      params,
      context,
    });
    // Explanation: SQLiteBasics interface expects SQLResultRow[]; all branches above produce compatible shapes
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return queryResult as SQLResultRow[];
  },

  async run(db: TauriSQL, { query, params, context }: SQLiteQueryWithParams): Promise<void> {
    const executeQuery = async ({ query, params, context }: SQLiteQueryWithParams) => {
      const start = performance.now();
      try {
        await db.execute(query, params);
      } catch (error) {
        logger.error(`QUERY ERROR: ${query}`, { params, context, error });
        throw error;
      }
      const duration = performance.now() - start;
      logger.debug(`EXECUTED QUERY (${duration.toFixed()}ms): ${query}`, { query, params, context });
    };
    // NOTE: have to manually batch queries into a transaction because to prevent data race where transactions
    // were committed before they were started... probably something to do with the Rust async runtime (tokio).
    if (query === 'BEGIN;') {
      if (transaction) {
        logger.error('BEGIN WHILE TRANSACTION ALREADY ONGOING', { transaction });
        return;
      }
      logger.debug('BEGINNING TRANSACTION');
      transaction = [];
    } else if (query === 'COMMIT;') {
      if (!transaction) {
        logger.error('COMMIT WHILE NO TRANSACTION ONGOING');
        return;
      }
      logger.debug(`COMMITTING TRANSACTION`);
      const statements = transaction.map(({ query }) => `${query};`).join('\n');
      const transactionParams = transaction.map(({ params }) => params).flat();
      const transactionQuery = `
        BEGIN;
        ${statements}
        COMMIT;
      `;
      transaction = undefined;
      await executeQuery({ query: transactionQuery, params: transactionParams, context });
    } else if (transaction) {
      logger.debug(`ADDING QUERY TO TRANSACTION: ${query}`, { transaction, query, params });
      transaction.push({ query, params, context });
    } else {
      // Single query without ongoing transaction.
      await executeQuery({ query, params, context });
    }
  },

  async setPragma(db: TauriSQL, key: string, value: string): Promise<void> {
    const stmt = `PRAGMA ${key}=${value};`;
    logger.debug('PRAGMA', { key, value, stmt });
    await db.execute(stmt);
  },

  async close(_db: TauriSQL): Promise<void> {
    logger.debug('DB CLOSE');
    // DON'T CLOSE DB SINCE RXDB IS STILL GONNA WANNA OPEN IT TO CLEAR EVERYTHING.
  },
};

export function getDesktopSQLiteRxStorage() {
  return getRxStorageSQLite({
    sqliteBasics: desktopSqliteBasics,
    // log: logger.debug.bind(logger),
  });
}
